/**
 * Smart Swirls Service
 * Handles fetching and managing Smart Swirls from deepsearch.smart_swirls table
 */

import { supabase } from '../lib/supabase';

class SmartSwirlsService {
  /**
   * Get all Smart Swirls for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of Smart Swirl objects
   */
  async getSmartSwirls(userId) {
    try {
      // Try deepsearch.smart_swirls first
      let query = supabase
        .schema('deepsearch')
        .from('smart_swirls')
        .select('*')
        .eq('user_id', userId)
        .eq('enabled', true)
        .order('created_at', { ascending: false });
      
      const { data, error } = await query;

      if (error) {
        // Handle missing table gracefully - try fallback
        if (error.code === 'PGRST116' || error.code === 'PGRST205' || error.code === 'PGRST301' || error.message?.includes('does not exist') || error.message?.includes('schema cache')) {
          // Fallback: try public.smart_swirls if it exists
          try {
            const { data: fallbackData, error: fallbackError } = await supabase
              .from('public.smart_swirls')
              .select('*')
              .eq('user_id', userId)
              .eq('enabled', true)
              .order('created_at', { ascending: false });
            
            if (!fallbackError && fallbackData) {
              console.log('✅ Using fallback smart_swirls table');
              return { data: fallbackData || [], error: null };
            }
          } catch (fallbackErr) {
            // Silently return empty - expected if schema not exposed
          }
          return { data: [], error: null };
        }
        // Only log unexpected errors
        if (import.meta.env.DEV) {
          console.warn('⚠️ Unexpected Smart Swirls error:', { code: error.code, message: error.message });
        }
        throw error;
      }

      // Enrich with metadata if items exist
      if (data && data.length > 0) {
        const enriched = await Promise.all(
          data.map(async (swirl) => {
            if (swirl.item_ids && Array.isArray(swirl.item_ids) && swirl.item_ids.length > 0) {
              // Filter out null/undefined IDs
              const validIds = swirl.item_ids.filter(id => id != null).slice(0, 10);
              if (validIds.length === 0) {
                return swirl; // Skip enrichment if no valid IDs
              }
              
              const { data: items } = await supabase
                .schema('deepsearch')
                .from('items')
                .select('tags, themes, tone, visual_tags, source_type, analysis_data')
                .in('id', validIds)
                .catch(() => ({ data: [] }));
              
              // Aggregate enriched metadata
              const allTags = [];
              const allThemes = [];
              const allTones = [];
              const allVisualTags = [];
              const sourceTypes = new Set();
              
              (items?.data || []).forEach(item => {
                if (item.analysis_data?.tags) {
                  allTags.push(...(Array.isArray(item.analysis_data.tags) ? item.analysis_data.tags : []));
                }
                if (item.analysis_data?.themes || item.themes) {
                  allThemes.push(...(Array.isArray(item.analysis_data?.themes || item.themes) ? (item.analysis_data?.themes || item.themes) : []));
                }
                if (item.analysis_data?.tone || item.tone) {
                  allTones.push(item.analysis_data?.tone || item.tone);
                }
                if (item.analysis_data?.visual_tags || item.visual_tags) {
                  allVisualTags.push(...(Array.isArray(item.analysis_data?.visual_tags || item.visual_tags) ? (item.analysis_data?.visual_tags || item.visual_tags) : []));
                }
                if (item.source_type) {
                  sourceTypes.add(item.source_type);
                }
              });
              
              return {
                ...swirl,
                tags: [...new Set(allTags)].slice(0, 10),
                themes: [...new Set(allThemes)].slice(0, 6),
                tone: allTones[0] || null,
                visual_tags: [...new Set(allVisualTags)].slice(0, 10),
                source_types: Array.from(sourceTypes)
              };
            }
            return swirl;
          })
        );
        
        return { data: enriched, error: null };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching Smart Swirls:', error);
      return { data: [], error };
    }
  }

  /**
   * Create a Smart Swirl cluster from items
   * @param {string} userId - User ID
   * @param {Array} items - Array of item objects to cluster
   * @param {Object} metadata - Additional metadata (title, description, color_palette, similarity_score)
   * @returns {Promise<Object>} Created Smart Swirl object
   */
  async createSmartSwirlCluster(userId, items, metadata = {}) {
    try {
      const itemIds = items.map(item => item.id || item);

      const swirlData = {
        user_id: userId,
        title: metadata.title || 'Untitled Swirl',
        description: metadata.description || null,
        item_ids: itemIds,
        color_palette: metadata.color_palette || {
          primary: '#A78BFA',
          gradient: ['#A78BFA', '#C084FC']
        },
        similarity_score: metadata.similarity_score || 0.0,
        enabled: true,
      };

      const { data, error } = await supabase
        .schema('deepsearch')
        .from('smart_swirls')
        .insert([swirlData])
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error creating Smart Swirl cluster:', error);
      return { data: null, error };
    }
  }

  /**
   * Update a Smart Swirl
   * @param {string} swirlId - Smart Swirl ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated Smart Swirl object
   */
  async updateSmartSwirl(swirlId, updates) {
    try {
      const { data, error } = await supabase
        .schema('deepsearch')
        .from('smart_swirls')
        .update(updates)
        .eq('id', swirlId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating Smart Swirl:', error);
      return { data: null, error };
    }
  }

  /**
   * Get items for a Smart Swirl (preview items)
   * @param {Array} itemIds - Array of item IDs
   * @param {number} limit - Maximum number of items to fetch (default: 3)
   * @returns {Promise<Array>} Array of item objects
   */
  async getSwirlItems(itemIds, limit = 3) {
    try {
      if (!itemIds || !Array.isArray(itemIds) || itemIds.length === 0) {
        return { data: [], error: null };
      }

      // Filter out any null/undefined IDs and ensure we have valid IDs
      const validIds = itemIds.filter(id => id != null).slice(0, limit);
      if (validIds.length === 0) {
        return { data: [], error: null };
      }

      const { data, error } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('id, title, description, url, source_type, auto_tags, ai_analysis, analysis_data, thumbnail_url')
        .in('id', validIds);

      if (error) {
        // Handle missing table gracefully
        if (error.code === 'PGRST116' || error.code === 'PGRST205') {
          return { data: [], error: null };
        }
        throw error;
      }

      // Check if items need enrichment (from instagram_content or other sources)
      if (data && data.length > 0) {
        const needsEnrichment = data.filter(item => 
          !item.analysis_data || 
          Object.keys(item.analysis_data || {}).length === 0
        );
        
        if (needsEnrichment.length > 0 && import.meta.env.DEV) {
          console.log(`🪄 ${needsEnrichment.length} Smart Swirl items need AI enrichment (analysis_data missing)`);
          // Note: Enrichment will happen automatically via deepsearchService fallback logic
        }
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching Swirl items:', error);
      return { data: [], error };
    }
  }

  /**
   * Get evolution data for a Smart Swirl
   * @param {string} swirlId - Smart Swirl ID
   * @returns {Promise<Array>} Array of evolution week data
   */
  async getSwirlEvolution(swirlId) {
    try {
      const { data, error } = await supabase
        .schema('deepsearch')
        .from('smart_swirls_evolution')
        .select('*')
        .eq('swirl_id', swirlId)
        .order('week_start', { ascending: true });

      if (error) {
        // Handle missing table gracefully
        if (error.code === 'PGRST116' || error.code === 'PGRST205' || error.message?.includes('does not exist')) {
          return { data: [], error: null };
        }
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching Swirl evolution:', error);
      return { data: [], error };
    }
  }

  /**
   * Get evolution highlights (peaks and significant changes)
   * @param {string} swirlId - Smart Swirl ID
   * @returns {Promise<Array>} Array of highlight weeks
   */
  async getSwirlEvolutionHighlights(swirlId) {
    try {
      const { data: evolution, error } = await this.getSwirlEvolution(swirlId);
      
      if (error) {
        return { data: [], error };
      }

      // Filter for peaks or significant changes
      const highlights = (evolution.data || []).filter(week => 
        week.new_items > 5 || week.avg_similarity > 0.85
      );

      return { data: highlights, error: null };
    } catch (error) {
      console.error('Error fetching Swirl evolution highlights:', error);
      return { data: [], error };
    }
  }

  /**
   * Cluster items by enriched metadata (Phase 10.2 + 10.3)
   * Uses weighted clustering: themes (0.35), tags embeddings (0.25), tone (0.20), visual_tags (0.20)
   * @param {Array} items - Array of item objects with enriched metadata
   * @returns {Promise<Array>} Array of clusters with similarity scores
   */
  async clusterByEnrichedMetadata(items) {
    try {
      if (!items || items.length < 2) {
        return [];
      }

      console.log('🔄 Clustering items by enriched metadata:', items.length);
      
      // Helper: Jaccard similarity for arrays
      const jaccardSimilarity = (arr1, arr2) => {
        if (!arr1 || !arr2 || arr1.length === 0 || arr2.length === 0) return 0;
        const set1 = new Set(arr1);
        const set2 = new Set(arr2);
        const intersection = new Set([...set1].filter(x => set2.has(x)));
        const union = new Set([...set1, ...set2]);
        return union.size === 0 ? 0 : intersection.size / union.size;
      };

      // Helper: Cosine similarity for embeddings (if available)
      const cosineSimilarity = (embedding1, embedding2) => {
        if (!embedding1 || !embedding2 || !Array.isArray(embedding1) || !Array.isArray(embedding2)) return 0;
        if (embedding1.length !== embedding2.length) return 0;
        
        let dotProduct = 0;
        let norm1 = 0;
        let norm2 = 0;
        
        for (let i = 0; i < embedding1.length; i++) {
          dotProduct += embedding1[i] * embedding2[i];
          norm1 += embedding1[i] * embedding1[i];
          norm2 += embedding2[i] * embedding2[i];
        }
        
        const denominator = Math.sqrt(norm1) * Math.sqrt(norm2);
        return denominator === 0 ? 0 : dotProduct / denominator;
      };

      // Calculate similarity scores between all pairs
      const clusters = [];
      const processed = new Set();

      for (let i = 0; i < items.length; i++) {
        if (processed.has(i)) continue;

        const cluster = {
          items: [items[i]],
          indices: [i],
          similarity_score: 1.0,
          metadata: {
            themes: items[i].themes || items[i].analysis_data?.themes || [],
            tags: items[i].tags || items[i].analysis_data?.tags || [],
            tone: items[i].tone || items[i].analysis_data?.tone || null,
            visual_tags: items[i].visual_tags || items[i].analysis_data?.visual_tags || [],
            source_types: [items[i].source_type].filter(Boolean)
          }
        };

        processed.add(i);

        // Find similar items
        for (let j = i + 1; j < items.length; j++) {
          if (processed.has(j)) continue;

          const item1 = items[i];
          const item2 = items[j];

          // Calculate weighted similarity
          const themes1 = item1.themes || item1.analysis_data?.themes || [];
          const themes2 = item2.themes || item2.analysis_data?.themes || [];
          const themesSim = jaccardSimilarity(themes1, themes2) * 0.35;

          const tags1 = item1.tags || item1.analysis_data?.tags || [];
          const tags2 = item2.tags || item2.analysis_data?.tags || [];
          // Use embeddings if available, otherwise Jaccard
          let tagsSim = 0;
          if (item1.embedding && item2.embedding) {
            tagsSim = cosineSimilarity(item1.embedding, item2.embedding) * 0.25;
          } else {
            tagsSim = jaccardSimilarity(tags1, tags2) * 0.25;
          }

          const tone1 = item1.tone || item1.analysis_data?.tone;
          const tone2 = item2.tone || item2.analysis_data?.tone;
          const toneSim = (tone1 && tone2 && tone1 === tone2) ? 0.20 : 0;

          const visualTags1 = item1.visual_tags || item1.analysis_data?.visual_tags || [];
          const visualTags2 = item2.visual_tags || item2.analysis_data?.visual_tags || [];
          const visualTagsSim = jaccardSimilarity(visualTags1, visualTags2) * 0.20;

          const totalSimilarity = themesSim + tagsSim + toneSim + visualTagsSim;

          // Add to cluster if similarity is high enough
          if (totalSimilarity > 0.5) {
            cluster.items.push(item2);
            cluster.indices.push(j);
            cluster.similarity_score = Math.max(cluster.similarity_score, totalSimilarity);
            
            // Merge metadata
            cluster.metadata.themes = [...new Set([...cluster.metadata.themes, ...themes2])];
            cluster.metadata.tags = [...new Set([...cluster.metadata.tags, ...tags2])];
            if (visualTags2.length > 0) {
              cluster.metadata.visual_tags = [...new Set([...cluster.metadata.visual_tags, ...visualTags2])];
            }
            if (item2.source_type) {
              cluster.metadata.source_types.push(item2.source_type);
            }
            
            processed.add(j);
          }
        }

        if (cluster.items.length > 1) {
          clusters.push(cluster);
        }
      }

      console.log('✅ Clustered into', clusters.length, 'groups');
      return clusters;

    } catch (error) {
      console.error('❌ Clustering error:', error);
      return [];
    }
  }

  /**
   * Get a single Smart Swirl by ID
   * @param {string} swirlId - Smart Swirl ID
   * @returns {Promise<Object>} Smart Swirl object
   */
  async getSmartSwirlById(swirlId) {
    try {
      const { data, error } = await supabase
        .schema('deepsearch')
        .from('smart_swirls')
        .select('*')
        .eq('id', swirlId)
        .single();

      if (error) {
        if (error.code === 'PGRST116' || error.code === 'PGRST205') {
          return { data: null, error: null };
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching Smart Swirl by ID:', error);
      return { data: null, error };
    }
  }

  /**
   * Get Smart Swirl insights with enriched item previews and extracted topics
   * Phase 9.3: Enriches swirls with actual content and extracts topics for recommendations
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of enriched Smart Swirl objects with preview items and topics
   */
  async getSmartSwirlInsights(userId) {
    try {
      const { data: swirls, error } = await this.getSmartSwirls(userId);
      
      if (error || !swirls || swirls.length === 0) {
        return [];
      }

      // Enrich each swirl with actual item previews + topics
      const enriched = await Promise.all(
        swirls.map(async (swirl) => {
          try {
            // Get preview items for this swirl
            const validIds = (swirl.item_ids || []).filter(id => id != null).slice(0, 3);
            
            if (import.meta.env.DEV) {
              console.log(`🔍 [DEBUG] Swirl "${swirl.title}" enrichment:`, {
                item_ids: swirl.item_ids,
                validIds: validIds,
                hasValidIds: validIds.length > 0
              });
            }
            
            if (validIds.length === 0) {
              // If no item_ids, try to find items by searching user content for topics matching swirl title
              if (import.meta.env.DEV) {
                console.log(`⚠️ Swirl "${swirl.title}" has no item_ids, attempting topic-based search...`);
              }
              
              // Try to find items that match the swirl title/topic from all user content
              try {
                if (import.meta.env.DEV) {
                  console.log(`🔍 [DEBUG] Searching instagram_content for items matching "${swirl.title}"...`);
                }
                
                const { data: matchingItems, error: searchError } = await supabase
                  .schema('public')
                  .from('instagram_content')
                  .select('id, title, description, ai_analysis, analysis_data')
                  .eq('user_id', userId)
                  .or(`title.ilike.%${swirl.title}%,description.ilike.%${swirl.title}%`)
                  .limit(5);
                
                if (searchError) {
                  if (import.meta.env.DEV) {
                    console.warn(`⚠️ Error searching for items matching "${swirl.title}":`, searchError);
                  }
                }
                
                if (import.meta.env.DEV) {
                  console.log(`🔍 [DEBUG] Search result for "${swirl.title}":`, {
                    itemsFound: matchingItems?.length || 0,
                    hasError: !!searchError
                  });
                }
                
                if (matchingItems && matchingItems.length > 0) {
                  if (import.meta.env.DEV) {
                    console.log(`✅ Found ${matchingItems.length} items matching swirl "${swirl.title}"`);
                  }
                  
                  // Extract topics from these matching items
                  const topics = [];
                  matchingItems.forEach(item => {
                    // Same extraction logic as below
                    // Extract topics from available fields (no tags column in instagram_content)
                    const itemTopics = item.analysis_data?.main_topics || 
                                      item.analysis_data?.topics || 
                                      item.analysis_data?.tags || 
                                      item.ai_analysis?.main_topics ||
                                      item.ai_analysis?.topics ||
                                      item.ai_analysis?.tags ||
                                      [];
                    
                    if (Array.isArray(itemTopics)) {
                      itemTopics.forEach(t => {
                        if (t && typeof t === 'string') topics.push(t.toLowerCase().trim());
                      });
                    }
                  });
                  
                  const uniqueTopics = [...new Set(topics)].slice(0, 3);
                  
                  if (import.meta.env.DEV) {
                    console.log(`🔍 [DEBUG] Extracted topics from matching items:`, uniqueTopics);
                  }
                  
                  return {
                    ...swirl,
                    preview: matchingItems.map(item => ({
                      id: item.id,
                      title: item.title,
                      description: item.description,
                      auto_tags: { topics: [] },
                      ai_analysis: item.ai_analysis,
                      analysis_data: item.analysis_data
                    })),
                    topics: uniqueTopics.length > 0 ? uniqueTopics : [swirl.title || 'General'],
                  };
                }
              } catch (searchError) {
                if (import.meta.env.DEV) {
                  console.warn(`Error searching for items matching swirl "${swirl.title}":`, searchError);
                }
              }
              
              // Fallback: return swirl with title as topic
              return {
                ...swirl,
                preview: [],
                topics: [swirl.title || 'General'],
              };
            }
            
            // Try deepsearch.items first
            let items = null;
            let itemsError = null;
            
            try {
              const { data, error } = await supabase
                .schema('deepsearch')
                .from('items')
                .select('id, title, description, tags, type, url, source_type, auto_tags, ai_analysis, analysis_data')
                .in('id', validIds);
              
              items = data;
              itemsError = error;
            } catch (e) {
              itemsError = e;
            }

            // Fallback to instagram_content if deepsearch.items fails or returns empty
            if ((itemsError || !items || items.length === 0) && validIds.length > 0) {
              if (import.meta.env.DEV) {
                console.log(`⚠️ deepsearch.items not available for swirl "${swirl.title}", trying instagram_content fallback`);
              }
              
              try {
                const { data: instagramItems } = await supabase
                  .schema('public')
                  .from('instagram_content')
                  .select('id, title, description, ai_analysis, analysis_data')
                  .in('id', validIds);
                
                if (instagramItems && instagramItems.length > 0) {
                  // Map instagram_content to expected format
                  items = instagramItems.map(item => ({
                    id: item.id,
                    title: item.title,
                    description: item.description,
                    auto_tags: {
                      topics: item.analysis_data?.main_topics || 
                              item.analysis_data?.topics || 
                              item.analysis_data?.tags || 
                              item.ai_analysis?.main_topics ||
                              item.ai_analysis?.topics ||
                              item.ai_analysis?.tags ||
                              (item.tags && Array.isArray(item.tags) ? item.tags : [])
                    },
                    ai_analysis: item.ai_analysis,
                    analysis_data: item.analysis_data,
                    tags: item.tags
                  }));
                  
                  if (import.meta.env.DEV) {
                    console.log(`✅ Using ${instagramItems.length} items from instagram_content for swirl "${swirl.title}"`);
                  }
                }
              } catch (fallbackError) {
                console.warn(`Error fetching instagram_content fallback for swirl ${swirl.id}:`, fallbackError);
              }
            }

            // Extract topics from items
            const topics = [];
            if (items && items.length > 0) {
              items.forEach(item => {
                // Priority 1: Extract from auto_tags.topics array
                if (item.auto_tags?.topics && Array.isArray(item.auto_tags.topics)) {
                  item.auto_tags.topics.forEach(t => {
                    if (t && typeof t === 'string') topics.push(t.toLowerCase().trim());
                  });
                }
                // Priority 2: Extract from ai_analysis
                if (item.ai_analysis) {
                  const aiTopics = item.ai_analysis.main_topics || 
                                  item.ai_analysis.topics || 
                                  item.ai_analysis.tags || 
                                  item.ai_analysis.themes || [];
                  if (Array.isArray(aiTopics)) {
                    aiTopics.forEach(t => {
                      if (t && typeof t === 'string') topics.push(t.toLowerCase().trim());
                    });
                  }
                }
                // Priority 3: Extract from analysis_data
                if (item.analysis_data) {
                  const dataTopics = item.analysis_data.main_topics || 
                                    item.analysis_data.topics || 
                                    item.analysis_data.tags || 
                                    item.analysis_data.themes || [];
                  if (Array.isArray(dataTopics)) {
                    dataTopics.forEach(t => {
                      if (t && typeof t === 'string') topics.push(t.toLowerCase().trim());
                    });
                  }
                }
                // Priority 4: Extract from tags array
                if (item.tags && Array.isArray(item.tags)) {
                  item.tags.forEach(t => {
                    if (t && typeof t === 'string') topics.push(t.toLowerCase().trim());
                  });
                }
              });
            }

            // Deduplicate and limit topics
            const uniqueTopics = [...new Set(topics)].slice(0, 3);
            
            if (import.meta.env.DEV) {
              console.log(`🔍 [DEBUG] Swirl "${swirl.title}" topic extraction:`, {
                itemsFound: items?.length || 0,
                topicsExtracted: uniqueTopics,
                fallbackUsed: !items || items.length === 0 ? 'swirl.title' : null
              });
            }
            
            // If no topics found, use swirl title as fallback (but this should be rare)
            const finalTopics = uniqueTopics.length > 0 ? uniqueTopics : [swirl.title || 'General'];

            return {
              ...swirl,
              preview: items || [],
              topics: finalTopics,
            };
          } catch (enrichError) {
            // If enrichment fails, return swirl with fallback topics
            console.warn(`Failed to enrich swirl ${swirl.id}:`, enrichError);
            return {
              ...swirl,
              preview: [],
              topics: [swirl.title || 'General'],
            };
          }
        })
      );

      if (import.meta.env.DEV) {
        console.log(`✅ Loaded ${enriched.length} Smart Swirls with insights`);
      }

      return enriched;
    } catch (error) {
      console.error('Error fetching Smart Swirl insights:', error);
      return [];
    }
  }

  /**
   * Adjust Smart Swirl weights based on behavioral signals
   * Phase 9.5.2b: Strengthens or weakens Smart Swirls based on user interactions
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  async adjustSmartSwirlWeights(userId) {
    try {
      const { data: behaviors, error } = await supabase
        .schema('deepsearch')
        .from('behavioral_signals')
        .select('source_swirl_id, interaction_type')
        .eq('user_id', userId);

      if (error) {
        if (error.code === 'PGRST116' || error.code === 'PGRST205' || error.message?.includes('does not exist')) {
          if (import.meta.env.DEV) {
            console.warn('⚠️ Behavioral signals table not available');
          }
          return;
        }
        throw error;
      }

      if (!behaviors || behaviors.length === 0) {
        return;
      }

      // Group and score behaviors
      const weights = {};
      behaviors.forEach(({ source_swirl_id, interaction_type }) => {
        if (!source_swirl_id) return;

        // Weight different interaction types
        const delta = 
          interaction_type === 'like' ? 1.0 :
          interaction_type === 'add_to_collection' ? 0.7 :
          interaction_type === 'view' ? 0.3 :
          interaction_type === 'dismiss' ? -0.5 : 0;

        weights[source_swirl_id] = (weights[source_swirl_id] || 0) + delta;
      });

      // Apply weight adjustments with decay factor
      const updates = [];
      for (const [swirlId, weight] of Object.entries(weights)) {
        if (Math.abs(weight) < 0.1) continue; // Skip tiny adjustments

        try {
          // Get current similarity_score
          const { data: swirl, error: fetchError } = await supabase
            .schema('deepsearch')
            .from('smart_swirls')
            .select('similarity_score')
            .eq('id', swirlId)
            .eq('user_id', userId)
            .single();

          if (fetchError || !swirl) {
            continue;
          }

          // Calculate new score with decay (older signals have less impact)
          const currentScore = swirl.similarity_score || 0.0;
          const adjustment = weight * 0.1; // 10% of weight delta
          const newScore = Math.max(0.0, Math.min(1.0, currentScore + adjustment));

          // Update similarity_score
          const { error: updateError } = await supabase
            .schema('deepsearch')
            .from('smart_swirls')
            .update({ similarity_score: newScore })
            .eq('id', swirlId)
            .eq('user_id', userId);

          if (updateError) {
            console.warn(`Failed to update swirl ${swirlId}:`, updateError);
          } else {
            updates.push({ swirlId, oldScore: currentScore, newScore, adjustment });
          }
        } catch (updateError) {
          console.warn(`Error updating swirl ${swirlId}:`, updateError);
        }
      }

      if (import.meta.env.DEV && updates.length > 0) {
        console.log(`✅ Adjusted ${updates.length} Smart Swirl weights based on behavioral signals`);
      }
    } catch (error) {
      console.error('Error adjusting Smart Swirl weights:', error);
    }
  }

  /**
   * Auto-generate Smart Swirls for a user
   * Phase 8.1: Triggers the edge function to generate Smart Swirls from user's items
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Result with swirls created count
   */
  async autoGenerateSmartSwirls(userId) {
    try {
      if (!userId) {
        return { success: false, error: 'User ID required' };
      }

      // Get Supabase client with auth
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) {
        return { success: false, error: 'Not authenticated' };
      }

      const token = session.session.access_token;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      if (!supabaseUrl) {
        console.error('VITE_SUPABASE_URL not configured');
        return { success: false, error: 'Supabase URL not configured' };
      }

      // Call the edge function
      const response = await fetch(`${supabaseUrl}/functions/v1/smart-swirls-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Edge function failed: ${errorText}`;
        
        // Handle schema cache errors more gracefully
        if (errorText.includes('schema cache') || errorText.includes('Could not find the table')) {
          errorMessage = `Smart Swirls generation temporarily unavailable. The database schema needs to be refreshed. Please try again in a few moments.`;
          if (import.meta.env.DEV) {
            console.warn('⚠️ Smart Swirls edge function error (schema cache):', errorText);
          }
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (import.meta.env.DEV) {
        console.log('✅ Smart Swirls auto-generated:', result);
      }

      return { success: true, ...result };
    } catch (error) {
      console.error('Error auto-generating Smart Swirls:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new SmartSwirlsService();

